import { useEffect, useRef, useState } from 'react'

import { sfx } from '../../../lib/sound'
import type { GameProps } from '../GameShell'

const W = 400
const H = 500
const PADDLE_W = 70
const PADDLE_H = 10
const BALL_R = 6
const ROWS = 5
const COLS = 8
const BRICK_H = 18
const GAP = 4
const LIVES = 3

interface BreakoutState {
  paddleX: number
  ballX: number
  ballY: number
  vx: number
  vy: number
  bricks: Brick[]
  lives: number
  score: number
  running: boolean
}

interface Brick {
  x: number
  y: number
  w: number
  alive: boolean
  row: number
}

function buildBricks(): Brick[] {
  const brickW = (W - GAP * (COLS + 1)) / COLS
  return Array.from({ length: ROWS * COLS }, (_, i) => {
    const row = Math.floor(i / COLS)
    const col = i % COLS
    return {
      x: GAP + col * (brickW + GAP),
      y: 50 + row * (BRICK_H + GAP),
      w: brickW,
      alive: true,
      row,
    }
  })
}

const ROW_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#6366f1']

export default function Breakout({ onGameOver, onScore }: GameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [lives, setLives] = useState(LIVES)
  const [score, setScore] = useState(0)

  // All mutable game state lives in refs: the loop runs at 60fps and driving it
  // through React state would re-render the tree on every frame.
  const state = useRef<BreakoutState>({
    paddleX: W / 2 - PADDLE_W / 2,
    ballX: W / 2,
    ballY: H - 60,
    vx: 3.2,
    vy: -3.2,
    bricks: buildBricks(),
    lives: LIVES,
    score: 0,
    running: true,
  })

  // Pointer and keyboard both steer the paddle.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    function moveTo(clientX: number) {
      const rect = canvas!.getBoundingClientRect()
      const relative = ((clientX - rect.left) / rect.width) * W
      state.current.paddleX = Math.max(
        0,
        Math.min(W - PADDLE_W, relative - PADDLE_W / 2),
      )
    }

    const onMouse = (e: MouseEvent) => moveTo(e.clientX)
    const onTouch = (e: TouchEvent) => {
      if (e.touches[0]) {
        e.preventDefault()
        moveTo(e.touches[0].clientX)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      const step = 26
      if (e.key === 'ArrowLeft' || e.key === 'a') {
        e.preventDefault()
        state.current.paddleX = Math.max(0, state.current.paddleX - step)
      }
      if (e.key === 'ArrowRight' || e.key === 'd') {
        e.preventDefault()
        state.current.paddleX = Math.min(W - PADDLE_W, state.current.paddleX + step)
      }
    }

    canvas.addEventListener('mousemove', onMouse)
    canvas.addEventListener('touchmove', onTouch, { passive: false })
    window.addEventListener('keydown', onKey)
    return () => {
      canvas.removeEventListener('mousemove', onMouse)
      canvas.removeEventListener('touchmove', onTouch)
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const maybeCtx = canvas?.getContext('2d')
    if (!canvas || !maybeCtx) return
    // Rebound with an explicit type: the narrowing from the guard above does
    // not reach inside the hoisted `step` function below.
    const ctx: CanvasRenderingContext2D = maybeCtx

    let frame = 0

    function resetBall() {
      const s = state.current
      s.ballX = W / 2
      s.ballY = H - 60
      s.vx = 3.2 * (Math.random() < 0.5 ? 1 : -1)
      s.vy = -3.2
    }

    function step() {
      const s = state.current
      if (!s.running) return

      s.ballX += s.vx
      s.ballY += s.vy

      // Walls.
      if (s.ballX < BALL_R || s.ballX > W - BALL_R) {
        s.vx *= -1
        s.ballX = Math.max(BALL_R, Math.min(W - BALL_R, s.ballX))
        sfx.tick()
      }
      if (s.ballY < BALL_R) {
        s.vy *= -1
        s.ballY = BALL_R
        sfx.tick()
      }

      // Paddle. The bounce angle depends on where it hits, which is what makes
      // the game steerable rather than a coin toss.
      const paddleTop = H - 30
      if (
        s.vy > 0 &&
        s.ballY + BALL_R >= paddleTop &&
        s.ballY + BALL_R <= paddleTop + PADDLE_H + Math.abs(s.vy) &&
        s.ballX >= s.paddleX &&
        s.ballX <= s.paddleX + PADDLE_W
      ) {
        const hit = (s.ballX - (s.paddleX + PADDLE_W / 2)) / (PADDLE_W / 2)
        const speed = Math.min(7, Math.hypot(s.vx, s.vy) * 1.02)
        const angle = hit * 1.05 // radians away from straight up
        s.vx = speed * Math.sin(angle)
        s.vy = -Math.abs(speed * Math.cos(angle))
        s.ballY = paddleTop - BALL_R
        sfx.pop()
      }

      // Bricks.
      for (const brick of s.bricks) {
        if (!brick.alive) continue
        if (
          s.ballX > brick.x &&
          s.ballX < brick.x + brick.w &&
          s.ballY > brick.y &&
          s.ballY < brick.y + BRICK_H
        ) {
          brick.alive = false
          s.vy *= -1
          // Higher rows are worth more.
          s.score += (ROWS - brick.row) * 10
          setScore(s.score)
          onScore?.(s.score)
          sfx.pop()
          break
        }
      }

      if (s.bricks.every((b) => !b.alive)) {
        s.running = false
        sfx.complete()
        onGameOver(s.score + s.lives * 100)
        return
      }

      // Dropped it.
      if (s.ballY > H) {
        s.lives -= 1
        setLives(s.lives)
        sfx.thud()
        if (s.lives <= 0) {
          s.running = false
          onGameOver(s.score)
          return
        }
        resetBall()
      }

      draw(ctx, s)
      frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    // Captured rather than read in cleanup: the ref object never changes, but
    // reading `.current` at teardown is the pattern that bites when it does.
    const game = state.current
    return () => {
      cancelAnimationFrame(frame)
      game.running = false
    }
  }, [onGameOver, onScore])

  return (
    <div className="space-y-3">
      <p className="text-center text-sm text-muted">
        {'❤️'.repeat(Math.max(0, lives))} · {score} points · move with the mouse or
        arrow keys
      </p>
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        className="mx-auto w-full max-w-sm touch-none rounded-xl border border-border-subtle bg-canvas"
      />
    </div>
  )
}

function draw(ctx: CanvasRenderingContext2D, s: BreakoutState) {
  ctx.clearRect(0, 0, W, H)

  for (const brick of s.bricks) {
    if (!brick.alive) continue
    ctx.fillStyle = ROW_COLORS[brick.row % ROW_COLORS.length]
    ctx.fillRect(brick.x, brick.y, brick.w, BRICK_H)
  }

  ctx.fillStyle = '#6366f1'
  ctx.fillRect(s.paddleX, H - 30, PADDLE_W, PADDLE_H)

  ctx.beginPath()
  ctx.arc(s.ballX, s.ballY, BALL_R, 0, Math.PI * 2)
  ctx.fillStyle = '#e5e7eb'
  ctx.fill()
}
